import React from "react";
import PropTypes from "prop-types";
import { DropdownButton, MenuItem, Button } from "react-bootstrap";
import { connect } from "react-redux";
import { injectIntl, intlShape, FormattedMessage } from "react-intl";
import { VictoryChart, VictoryLine, VictoryBar, VictoryAxis,
	/*voronoiContainerMixin,*/ VictoryContainer, VictoryTooltip, Bar } from "victory";
import { makeCreateContainerFunction } from "victory-chart";
import _, { assign } from "lodash";

import { X_MODES } from "../model/Diagramme";
import { STATUS_MOTOR_MASK, STATUS_ERROR_MASK, STATUS_ON_LOAD_MASK,
	STATUS_COMM_ERROR_MASK, STATUS_NO_STATUS, compressorNameOrNumber } from "../model/Kompressor";

import { setXRangeAction, setYRangeAction } from "../model/Diagramme";

import { getUnit } from "../einheiten/Units";
import AlmigTheme from "../grundelemente/AlmigTheme";

import { almigBisectContainerMixin } from "./VictoryAlmigBisectContainer";
import { almigZoomContainerMixin } from "./VictoryAlmigZoomContainer";

import FormSetNumber from "./FormSetNumber";
import ButtonSetDateAndTime from "./ButtonSetDateAndTime";

import { formatValue, formatDate, formatValueUnit } from "./GrafikenCommon"

import "./Grafiken.css";

const renderYValue = (y_value, key, change_fct) => (
	<MenuItem key={key} onClick={ () => { change_fct(y_value.index); }}>
		<FormattedMessage id={y_value.i18n_key} />
	</MenuItem>
);

const renderYComponent = (y_component, key, change_fct, intl) => (
	<MenuItem key={key} onClick={ () => { change_fct(y_component.index); }}>
		{ y_component.makeLabel(intl) }
	</MenuItem>
);

const getYComponent = (y_components, y_component_index) => {
	let y_component = null;
	y_components.forEach(component => {
		if (component.index === y_component_index) {
			y_component = component;
		}
	});
	return y_component;
};

const makeYChosenComponent = (y_components, y_component_index, intl) => {
	let y_component = getYComponent(y_components, y_component_index);
	return y_component && y_component.makeLabel(intl);
};

const makeYLabel = (y_components, y_component_index, y_value, intl, units) => {
	let y_component = getYComponent(y_components, y_component_index);
	let label = y_component
		? y_component.makeLabel(intl) + " : "
		: "";
	label += intl.formatMessage({ id: y_value.i18n_key }) + " [" +
		getUnit(units[y_value.unit]).genUnitText(intl) + "]";
	return label;
};

const Container = makeCreateContainerFunction({
	bisect: [almigBisectContainerMixin],
	zoom: [almigZoomContainerMixin]
}, VictoryContainer)("zoom", "bisect");

const verifyYDomain = (domain, y_value) => {
	if (domain[1] - domain[0] < y_value.y_step) {
		domain[1] = domain[0] + y_value.y_step;
	}
	return domain.map(value => Math.max(value, y_value.y_min));
};

const verifyXDomain = (domain) => {
	return domain;
};

const getFillColor = datum => {
	if ((datum.state & STATUS_COMM_ERROR_MASK) || (datum.state === STATUS_NO_STATUS)) {
		return "#4b4b4b";
	}
	if (datum.state & STATUS_ERROR_MASK) {
		return "#ee322e";
	}
	if (datum.state & STATUS_MOTOR_MASK) {
		return datum.state & STATUS_ON_LOAD_MASK ?
			"#067bba" : "#87daff";
	}
	return "#929292";	/* aus */
};

const CompressorStatusBar = props => (
	props.datum.state !== 0x10000 ?	/* installed? */
		<Bar x={props.x} y={props.y} y0={props.y0} style={{
			fill: getFillColor(props.datum),
			width: props.barWidth
		}} scale={props.scale} data={props.data} horizontal />
		: null
);

CompressorStatusBar.propTypes = {
	x: PropTypes.number,
	y: PropTypes.number,
	y0: PropTypes.number,
	datum: PropTypes.object,
	data: PropTypes.arrayOf(PropTypes.object),
	scale: PropTypes.object,
	barWidth: PropTypes.number
};

const NoTickLabel = () => null;

const compressorLabel = (d, compressors) => {
	return d ? compressorNameOrNumber(compressors[d]) : "";
};


const CoreGraph = props => (
	<VictoryChart domain={{ x: props.x_range, y: props.y_range }}
		padding={{ top: 45, bottom: 45, left: 40, right: 40 /* wegen Datum+Uhrzeit und VictoryTooltip */ }}
		theme={AlmigTheme}
		scale={{x: "time"}} containerComponent={
			<Container labels={datum => formatDate(datum.x, props.intl) + "\n" +
					formatValue(datum.y, props.y_value, props.units, props.intl) + " " +
					formatValueUnit(props.y_value, props.units, props.intl)}
			onDomainChange={domain => {
				props.changeXRange(verifyXDomain([domain.x[0], domain.x[1]]));
				props.changeYRange(verifyYDomain(domain.y, props.y_value));
			}}
			allowWheel={true}
			activateData={false}
			activateLabels={false}
			labelComponent={<VictoryTooltip/>} />} >
		<VictoryAxis 
			tickCount={4}
			tickFormat={x => formatDate(x, props.intl, props.x_range, props.tz)} 
			fixLabelOverlap={true}
		/>
		<VictoryAxis dependentAxis
			style={assign({}, AlmigTheme.axis.style, {
				grid: { stroke: "lightgrey" },
				ticks: { size: 0 }
			})}
			tickFormat={() => null}
			label={makeYLabel(props.y_components, props.y_component_index, props.y_value, props.intl, props.units)}/>
		<VictoryLine data={_.cloneDeep(props.log_values)} x="date" y={props.y_value.path}/>
	</VictoryChart>
);

CoreGraph.propTypes = {
	log_values: PropTypes.arrayOf(PropTypes.object).isRequired,
	x_range: PropTypes.array.isRequired,
	y_range: PropTypes.array,	/* kann null sein, wenn Auto-Y */
	y_values: PropTypes.arrayOf(PropTypes.object).isRequired,
	y_value: PropTypes.object.isRequired,
	y_components: PropTypes.arrayOf(PropTypes.object).isRequired,
	y_component_index: PropTypes.number.isRequired,
	changeXRange: PropTypes.func.isRequired,
	changeYRange: PropTypes.func.isRequired,
	units: PropTypes.object.isRequired,
	intl: intlShape.isRequired,
	tz: PropTypes.string.isRequired
};

/*
 * hack: das log_values object wird gecloned damit der update-mechanismus
 *   des linecharts anspringt. sonst zeichnet er gefuehlt "unsichtbar"  weiter :-()
 */
const CoreGraphContainer =
	injectIntl(
		connect(state => ({
			log_values: state.charts.log_values,
			x_range: state.charts.x_range,
			y_values: state.charts.y_values,
			y_value: state.charts.y_value,
			y_component_index: state.charts.y_component_index,
			units: state.units,
			tz: state.ablauf.system.tz
		}), (dispatch, own_props) => ({
			changeXRange: range => {
				dispatch(setXRangeAction(range));
			},
			changeYRange: range => {
				dispatch(setYRangeAction(range));
			}
		}))(CoreGraph)
	);

const Grafiken = props => {
	const { state_changes, y_values, y_value, y_components, y_component_index,
		x_range, x_range_valid, x_mode, y_range, changeYValue, changeYComponent,
		activateYAuto, changeXMode, show_compressor_state, compressors,
		units, intl, moveX, moveY, setNewRangeY, setNewRangeX,
		dataSourceOfCharts, heDataLogFilesRequestState, setDataSourceOfCharts
	} = props;

	const { bar_width, domain_padding } = props.bar_params;

	return (
		<div className="Linechart printableAlmigItem">
			<div>
				<div>
					<DropdownButton id="charts_y_value"
						title={intl.formatMessage({ id: y_value.i18n_key})}>
						{ y_values.map((y_value, i) => (
							renderYValue(y_value, i, changeYValue)
						))}
					</DropdownButton>
					<DropdownButton id="charts_y_component"
						title={makeYChosenComponent(y_components,
							y_component_index, intl)}>
						{ y_components.map((y_component, i) => (
							renderYComponent(y_component, i, changeYComponent, intl)
						))}
					</DropdownButton>
				</div>
			</div>
			<div className="Y-Extra-and-Chart">
				<div className="Y-Extra">
					<div className="Y-Upper-Limit">
						<FormSetNumber
							externValue={y_range[1]}
							validRange={[y_range[0], null]}
							y_value={y_value}
							units={units}
							doIfValid={(newValue) => { setNewRangeY([y_range[0], newValue]); }}
						/>
					</div>
					<div className="Y-Up">
						<Button id="UpY" onClick={ () => { moveY(y_range, 25); } }/>
					</div>
					<div className="Y-Auto">
						<Button id="AutoY" onClick={activateYAuto} />
					</div>
					<div className="Y-Down">
						<Button id="DownY" onClick={ () => { moveY(y_range, -25); } }/>
					</div>
					<div className="Y-Lower-Limit">
						<FormSetNumber
							externValue={y_range[0]}
							validRange={[null, y_range[1]]}
							y_value={y_value}
							units={units}
							doIfValid={(newValue) => { setNewRangeY([newValue, y_range[1]]); }}
						/>
					</div>
				</div>
				<CoreGraphContainer
					y_range={y_range}
					y_components={y_components}
					y_component_index={y_component_index} 
				/>
			</div>
			{ !show_compressor_state ? null :
				<VictoryChart domain={{ x: x_range }}
					theme={AlmigTheme} height={200}
					padding={{ left: 40, top: 5, bottom: 5 }}
					domainPadding={{ y: domain_padding }}
					scale={{x: "time"}}>
					<VictoryAxis tickLabelComponent={<NoTickLabel />} />
					<VictoryAxis dependentAxis tickFormat={
						d => compressorLabel(d, compressors)} />
					<VictoryBar data={state_changes.changes} horizontal
						x={d => d.cindex.toString()} y0="start" y="end"
						dataComponent={<CompressorStatusBar barWidth={bar_width} />} />
				</VictoryChart>
			}
			{/* Zum Test "Tooltip auf horizontalem Bar-Chart"
				* Ergebnis bisher: keine Ahnung, warum die Tooltips oben nicht angezeigt werden.
				* Hier sind sie etwas "off-position" (links oben)
				labelComponent={<VictoryTooltip text={() => {
								return "hello";
							}} width={100}/>}

			<VictoryChart theme={AlmigTheme}
				domain={{ x: x_range }}
				scale={{x: "time" }}>
				<VictoryAxis />
				<VictoryAxis dependentAxis />
				<VictoryBar horizontal labelComponent={<VictoryTooltip text={datum =>  {
					return datum ? datum.state.toString() + "hello" : "hello";
				}} />} data={[
					{x: 2, y: x_range[0] + 10000, y0: x_range[0] + 2000, state: 0 },
					{x: 2, y: x_range[0] +  2000, y0: x_range[0] +  1000, state: 1 },
					{x: 4, y: x_range[1], y0: x_range[0], state: 2 },
					{x: 6, y: 4, state: 3},
					{x: 8, y: 5, state: 4}]} />
			</VictoryChart> */}
			<div id="XContainer">
				<div id="LeftXTimeContainer">
					<ButtonSetDateAndTime
						validRange={[x_range_valid[0], x_range[1] - 1000]}
						currentDateTimeMs={x_range[0]}
						acceptNewDate={newDate => {setNewRangeX([newDate, x_range[1]]);}}
						popupPlacement="top"
						dataSourceOfCharts={dataSourceOfCharts}
						setDataSourceOfCharts={setDataSourceOfCharts}
					/>
				</div>
				<div id="LeftXContainer">
					<Button id="LeftX"
						onClick={ () => { moveX(x_range, -25); } }/>
				</div>
				<div id="CenterXContainer">
					<DropdownButton id="charts_x_mode"
						title={intl.formatMessage({ id: x_mode })}>
						{ X_MODES.map(mode => (
							<MenuItem key={mode} onClick={ () => { changeXMode(mode); }}>
								<FormattedMessage id={mode} />
							</MenuItem>
						))}
					</DropdownButton>
				</div>
				<div id="RightXContainer">
					<Button id="RightX"
						onClick={ () => { moveX(x_range, 25); } }/>
				</div>
				<div id="RightXTimeContainer">
					<ButtonSetDateAndTime
						validRange={[x_range[0] + 1000, x_range_valid[1]]}
						currentDateTimeMs={x_range[1]}
						acceptNewDate={newDate => {setNewRangeX([x_range[0], newDate]);}}
						popupPlacement="top"
						dataSourceOfCharts={dataSourceOfCharts}
						setDataSourceOfCharts={setDataSourceOfCharts}
					/>
				</div>
			</div>
			<div>
				<FormattedMessage id={heDataLogFilesRequestState} />
			</div>
		</div>
	);
};

Grafiken.propTypes = {
	state_changes: PropTypes.object.isRequired,
	y_values: PropTypes.arrayOf(PropTypes.object).isRequired,
	y_value: PropTypes.object.isRequired,
	y_components: PropTypes.arrayOf(PropTypes.object).isRequired,
	y_component_index: PropTypes.number.isRequired,
	x_range: PropTypes.array.isRequired,
	x_mode: PropTypes.string.isRequired,
	y_range: PropTypes.array,	/* kann null sein, wenn Auto-Y */
	changeYValue: PropTypes.func.isRequired,
	changeYComponent: PropTypes.func.isRequired,
	moveX: PropTypes.func.isRequired,
	moveY: PropTypes.func.isRequired,
	setNewRangeY: PropTypes.func.isRequired,
	setNewRangeX: PropTypes.func.isRequired,
	activateYAuto: PropTypes.func.isRequired,
	changeXMode: PropTypes.func.isRequired,
	show_compressor_state: PropTypes.bool.isRequired,
	compressors: PropTypes.arrayOf(PropTypes.object).isRequired,
	bar_params: PropTypes.object.isRequired,
	units: PropTypes.object.isRequired,
	dataSourceOfCharts: PropTypes.number.isRequired,
	heDataLogFilesRequestState: PropTypes.string.isRequired,
	setDataSourceOfCharts: PropTypes.func.isRequired,
	intl: intlShape.isRequired
};

export default injectIntl(Grafiken);
